import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Pagination,
  Select,
  MenuItem,
  TextField,
  Button,
  Skeleton,
  Alert,
  IconButton,
  SelectChangeEvent,
  Grid,
} from '@mui/material';
import { Clear, Camera as CameraIcon, Sort, HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import { UserMemory } from '../types/Memory';
import { UserProfile } from '../types/UserProfile';
import { getUserMemories } from '../services/api';
import MemoryItem from './memory/MemoryItem';
import { useTheme } from '@mui/material/styles';
import { useSearchParams, useParams } from 'react-router-dom';
import AdvertisementResponsive from './advertisement/Ad_GA_res';
import Tips from './dialogs/Tips';

interface MemoriesTabProps {
  username?: string;
  isPublic?: boolean;
  userProfile?: UserProfile | null;
}

const ITEMS_PER_PAGE = 10;

const MemoriesTab: React.FC<MemoriesTabProps> = ({ username: usernameProp, isPublic = false, userProfile = null }) => {
  const theme = useTheme();
  const { username: usernameParam } = useParams<{ username: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const stableUsername = isPublic ? usernameParam : 'me';
  const username = isPublic ? usernameParam : usernameProp;

  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortOrder, setSortOrder] = useState('default');

  // URLパラメータから初期値を設定
  useEffect(() => {
    const queryParam = searchParams.get('query') || '';
    const orderParam = searchParams.get('order') || 'default';
    setSearchQuery(queryParam);
    setSearchInput(queryParam);
    setSortOrder(orderParam);
  }, []);

  // React Query でメモリー取得
  const { data: memoriesData, isLoading, isError } = useQuery({
    queryKey: ['memories', stableUsername, page, searchQuery, sortOrder],
    queryFn: () => getUserMemories(username!, page, searchQuery, sortOrder),
    enabled: !!username,
    staleTime: isPublic ? 30000 : 2000,
  });

  const memories: UserMemory[] = memoriesData?.data.results || [];
  const totalCount = memoriesData?.data.count || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const loading = isLoading && !memoriesData;

  // 並び替えオプション
  const sortOptions = [
    { value: 'default', label: '投稿日時 (新しい順)' },
    { value: 'created_at_asc', label: '投稿日時 (古い順)' },
    { value: 'play_date_desc', label: 'プレイ時期 (新しい順)' },
    { value: 'play_date_asc', label: 'プレイ時期 (古い順)' },
    { value: 'updated_at_desc', label: '更新日時 (新しい順)' },
    { value: 'updated_at_asc', label: '更新日時 (古い順)' },
  ];

  const handleSortChange = (event: SelectChangeEvent) => {
    setSortOrder(event.target.value);
    setPage(1);
    setSearchParams(params => {
      params.set('page', '1');
      if (event.target.value !== 'default') {
        params.set('order', event.target.value);
      } else {
        params.delete('order');
      }
      return params;
    });
  };

  const handleSearch = (newQuery: string) => {
    setSearchQuery(newQuery);
    setPage(1);
    setSearchParams(params => {
      params.set('page', '1');
      if (newQuery) {
        params.set('query', newQuery);
      } else {
        params.delete('query');
      }
      return params;
    });
  };

  const handleClearFilter = () => {
    setSearchInput('');
    setSearchQuery('');
    setSortOrder('default');
    setPage(1);
    setSearchParams(params => {
      params.set('page', '1');
      params.delete('query');
      params.delete('order');
      return params;
    });
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    setSearchParams(params => {
      params.set('page', value.toString());
      return params;
    });
  };

  const handleMemoryUpdate = () => {
    // メモリーが更新された際のコールバック（いいねなど）
  };

  const MemoryLoadingPlaceholder = () => {
    return (
      <Box>
        {[...Array(3)].map((_, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Skeleton
              variant="rectangular"
              height={140}
              sx={{
                borderRadius: 2,
                bgcolor: 'rgba(255, 255, 255, 0.05)'
              }}
            />
          </Box>
        ))}
      </Box>
    );
  };

  const MemoryTipsContent = () => (
    <Box sx={{ maxWidth: 400 }}>
      <Typography variant="body2" paragraph>
        メモリーは、ゲームを評価（レビュー）ではなく、思い出ベースで記録できる機能です。
      </Typography>
      <Typography variant="body2" paragraph>
        当時のことを思い返して、そのゲームにまつわる体験や印象を自由に記録してみましょう。
      </Typography>
      <Typography variant="body2" paragraph>
        使い方：
      </Typography>
      <Box component="ol" sx={{ pl: 2, mt: 1 }}>
        <li>
          <Typography variant="body2" sx={{ mb: 1 }}>
            各ゲームページのメモリーボタン（<CameraIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} />）をクリックしてダイアログを開きます
          </Typography>
        </li>
        <li>
          <Typography variant="body2" sx={{ mb: 1 }}>
            内容を入力して思い出を記録します
          </Typography>
        </li>
        <li>
          <Typography variant="body2" sx={{ mb: 1 }}>
            ゲームステータスのプレイ日時が自動で適用されます。予めゲームをライブラリに保存する必要があります。
          </Typography>
        </li>
        <li>
          <Typography variant="body2" sx={{ mb: 1 }}>
            ネタバレを含む部分は必ずスポイラータグ（フォーマット参照）で囲んでください。
          </Typography>
        </li>
      </Box>
      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontSize: '0.7rem' }}>
        ※メモリーは現在プロフィールページでのみ表示されますが、将来的には新着メモリーや検索など、他のページでも表示される予定です。
      </Typography>
    </Box>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={8.5}>
        {/* フィルター・検索・並び替えエリア */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              placeholder="Search..."
              variant="standard"
              size="small"
              value={searchInput}
              onChange={(e) => {
                const newValue = e.target.value;
                setSearchInput(newValue);
                handleSearch(newValue);
              }}
              sx={{
                flexGrow: 1,
                '& .MuiInputBase-input': {
                  ...theme.typography.body2,
                },
                '& .MuiInputBase-input::placeholder': {
                  opacity: 0.5
                },
                '& .MuiInputBase-root:before': {
                  borderBottomColor: 'rgba(255, 255, 255, 0.5)',
                },
                '& .MuiInputBase-root:hover:not(.Mui-disabled):before': {
                  borderBottomColor: 'rgba(255, 255, 255, 0.8)',
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Select
                value={sortOrder}
                onChange={handleSortChange}
                displayEmpty
                sx={{
                  minWidth: 100,
                  ...theme.typography.body2,
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '4px',
                  '& .MuiSelect-select': {
                    paddingY: 0.5,
                    paddingX: 1,
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.secondary',
                    fontSize: { xs: '0.65rem', sm: '0.8rem' },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  }
                }}
                renderValue={(selected) => {
                  const selectedOption = sortOptions.find(option => option.value === selected);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Sort sx={{
                        mr: 0.5,
                        fontSize: '1rem',
                        display: { xs: 'none', sm: 'block' }
                      }} />
                      {selectedOption ? selectedOption.label : 'デフォルト'}
                    </Box>
                  );
                }}
              >
                {sortOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value} dense>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <Tips
                title="TIPS:メモリー"
                content={<MemoryTipsContent />}
                buttonComponent={
                  <IconButton
                    size="small"
                    sx={{
                      padding: '4px',
                      opacity: 0.7,
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                  >
                    <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                }
              />
            </Box>
          </Box>
          {searchQuery !== '' && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {totalCount}件のメモリーが見つかりました
              </Typography>
              <Button
                size="small"
                onClick={handleClearFilter}
                startIcon={<Clear fontSize="small" />}
                sx={{ ...theme.typography.body2, textTransform: 'none', p: '2px 8px' }}
              >
                Clear filter
              </Button>
            </Box>
          )}
        </Box>

        {/* メモリーリスト */}
        {loading ? (
          <MemoryLoadingPlaceholder />
        ) : isError ? (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              bgcolor: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              color: '#FFCDD2',
              fontSize: '0.8rem'
            }}
          >
            メモリーの読み込みに失敗しました。
          </Alert>
        ) : memories.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              textAlign: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2
            }}>
              <CameraIcon sx={{ fontSize: 32, color: 'rgba(255, 255, 255, 0.4)' }} />
            </Box>
            <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 0.5, fontWeight: 400, fontSize: '0.85rem' }}>
              {searchQuery || sortOrder !== 'default'
                ? '条件に一致するメモリーが見つかりませんでした'
                : 'まだメモリーが投稿されていません'
              }
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
              {!isPublic && !searchQuery && sortOrder === 'default' &&
                'ゲームの思い出を記録してみましょう！'
              }
            </Typography>
          </Box>
        ) : (
          <Box>
            {memories.map((memory) => (
              <MemoryItem
                key={memory.id}
                memory={memory}
                onUpdate={handleMemoryUpdate}
                showUserInfo={false}
                userProfile={userProfile}
                isPublic={isPublic}
              />
            ))}

            {/* ページネーション */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  size="medium"
                />
              </Box>
            )}
          </Box>
        )}
      </Grid>

      {/* 右カラムに広告エリア */}
      <Grid item xs={12} md={3.5} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Box sx={{ position: 'sticky', top: 80 }}>
          <AdvertisementResponsive />
        </Box>
      </Grid>
    </Grid>
  );
};

export default MemoriesTab;
